/**
 * resolvedDomainUrl — single source of truth for patient-facing URLs.
 *
 * Replaces the five places that used to inline the same custom-subdomain
 * logic (ConsentV2Link, ConsentV2LinkDialog, SendFormsPreview,
 * surveyConstants, quickSendUtils). The hosting plumbing lives in
 * apps/domains on the backend; this module is the FE half of that
 * contract. Same precedence, same lowercase normalisation, same
 * fallback to window.location.origin on localhost so dev-mode links
 * remain testable.
 *
 * Two exports:
 *
 *   buildResolvedDomainUrl({ consentConfig, pathPrefix, token })
 *     Pure function. Builds the production URL when consentConfig has
 *     a custom_subdomain configured; falls back to the current origin
 *     otherwise. Used by both React-component callers and the legacy
 *     utility-shape callers (surveyConstants, quickSendUtils).
 *
 *   isLocalhost()
 *     Centralised host check so every caller agrees on what 'dev mode'
 *     means. We were previously inlining this in three places.
 *
 * Why this lives in src/utils/ and not src/hooks/:
 *   The pure utility shape is the load-bearing API — surveyConstants
 *   and quickSendUtils are imported by non-component code (payload
 *   builders, step files) and cannot use hooks. The
 *   ``useResolvedDomain`` hook in src/hooks/ is a thin wrapper that
 *   fetches the consentConfig and then delegates to this function.
 */

export const isLocalhost = () =>
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

const DEFAULT_BRANDED_BASE_DOMAIN = 'patient-hub.co.uk';

/**
 * Build a patient-facing URL from a practice's consent_form_config.
 *
 * Precedence — mirrors apps.domains.services.resolve_domain on the BE:
 *   1. consentConfig has url_style='custom_subdomain' + a subdomain
 *      → https://{subdomain}.{baseDomain}/{pathPrefix}/{token}
 *   2. otherwise → {window.location.origin}/{pathPrefix}/{token}
 *
 * @param {object}   options
 * @param {object}   options.consentConfig  The PracticeChoice row with
 *                                          metadata.type='consent_form_config'.
 *                                          May be null/undefined.
 * @param {string}   options.pathPrefix     'consent' | 'survey' | other.
 *                                          Trimmed of leading/trailing
 *                                          slashes before insertion.
 * @param {string}   options.token          Access token / slug / id.
 * @returns {string}                        Absolute URL, or '' when token
 *                                          is falsy.
 */
export function buildResolvedDomainUrl({ consentConfig, pathPrefix, token }) {
    if (!token) return '';
    const cleanPrefix = (pathPrefix || '').replace(/^\/+|\/+$/g, '');
    const path = cleanPrefix ? `${cleanPrefix}/${token}` : String(token);

    const meta = consentConfig?.metadata || {};
    const rawMeta = typeof meta === 'string'
        ? (() => {
            try { return JSON.parse(meta || '{}'); } catch { return {}; }
        })()
        : meta;

    const sub = (rawMeta.custom_subdomain || '').trim().toLowerCase();
    if (rawMeta.url_style === 'custom_subdomain' && sub) {
        const baseDomain = (rawMeta.base_domain || DEFAULT_BRANDED_BASE_DOMAIN)
            .replace(/^\.+|\/+$/g, '');
        return `https://${sub}.${baseDomain}/${path}`;
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/${path}`;
}

/**
 * On localhost with a custom_subdomain config, return the local dev URL
 * for testing. Returns null off-localhost or when not configured.
 *
 * The Send-Forms wizard's success view shows this alongside the real
 * URL so developers can open the link locally without flipping any
 * config. Mirrors the comment in apps/practice/form_link.py describing
 * the "Dev test link" affordance.
 */
export function buildDevDomainUrl({ pathPrefix, token }) {
    if (!isLocalhost() || !token) return null;
    const cleanPrefix = (pathPrefix || '').replace(/^\/+|\/+$/g, '');
    const path = cleanPrefix ? `${cleanPrefix}/${token}` : String(token);
    return `${window.location.origin}/${path}`;
}
